import { memo, useMemo } from "react";

import { useTranslator } from "@eo-locale/react";

import { useCallback, useState } from "react";

import { isEmpty } from "lodash";

import {
  Box,
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  Select,
  Text
} from "@/storybook-components";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import { setMarker, setMarkerError } from "@/redux/features/place-marker/slice";
import PinIcon from "../markers/pin-Icon";

export interface SelectPin {
  symbols: Array<Symbols>;
}
export interface Symbols {
  icon: any;
  value: string;
  name: string;
  category: Category;
  color: string;
}

export interface Category {
  name: string;
  color?: string;
  icon?: string;
  visible?: boolean;
  answered?: boolean;
  id: string;
  categoryId?: string;
}

const SelectPins = (props: SelectPin) => {
  const dispatch = useAppDispatch();
  const symbolsData: Symbols = props.symbols[0];
  const symbolsMemo: any = useMemo(() => {
    return {
      label: symbolsData?.name,
      color: symbolsData?.color,
      value: symbolsData?.value,
      iconName: symbolsData?.category?.icon,
      categoryId: symbolsData?.category?.id
    };
  }, [props.symbols]);
  const [selectionState, setSelectionState] = useState<any>(
    props.symbols.length === 1 ? symbolsMemo : {}
  );
  const { marker } = useAppSelector((state) => state.placesMarkerSlice);
  const { categoryId: markerId } = marker;
  const categories = useAppSelector(
    (state) => state.markerCategories.categories
  );
  const getPinOptions = () => {
    return props.symbols.map((category: Symbols) => {
      const value = category.category.id;
      const label = category.name;
      const iconName = category.value;
      const color = category.color;
      return {
        color,
        value,
        label,
        iconName,
        categoryId: category.category.id
      };
    });
  };

  const handleSelect = async (selectedOption) => {
    dispatch(setMarkerError(""));
    setSelectionState(selectedOption);

    const updatedMarkerData = {
      ...marker,
      pinIcon: selectedOption.iconName,
      categoryId: selectedOption.categoryId
    };

    dispatch(
      setMarker({
        ...updatedMarkerData
      })
    );
  };

  const renderOptionLabel = ({ label, iconName, color, id }) => {
    const pinIconProps = {
      pinIcon: iconName,
      pinColor: color,
      pinId: id
    };
    return (
      <Box display="flex" alignItems="center" key={id}>
        <PinIcon {...pinIconProps} />
        <Text pl={2}> {label} </Text>
      </Box>
    );
  };

  const error = useAppSelector((state) => state.placesMarkerSlice.error);
  const { translate } = useTranslator();

  const isSelectPinsEmpty = error.length > 0 && error === "empty";

  const getValue = useCallback(() => {
    const filteredCategory: Category = categories.find(
      (c) => +c.id === markerId
    );

    if (!isEmpty(selectionState)) {
      return {
        label: selectionState.label,
        color: selectionState.color,
        value: selectionState.value,
        iconName: selectionState.iconName,
        categoryId: selectionState.categoryId
      };
    } else if (filteredCategory) {
      return {
        label: filteredCategory.name,
        value: filteredCategory.id,
        iconName: filteredCategory.icon,
        categoryId: filteredCategory.id,
        color: filteredCategory.color
      };
    }

    return "";
  }, [selectionState, categories]);

  return (
    <Box>
      <FormControl
        mt={2}
        isInvalid={isSelectPinsEmpty}
        className={error.length ? "ehqQuestionError" : null}
      >
        <Select
          className="pin-selector"
          backgroundColor={"white"}
          value={getValue()}
          options={getPinOptions()}
          isSearchable={false}
          placeholder={translate("places.pin.selectPinPlaceholderText")}
          onChange={handleSelect} // Using onChange instead of onSelect as per react-select documentation
          formatOptionLabel={renderOptionLabel}
          getOptionLabel={(option) => option.label}
        />
        {isSelectPinsEmpty && (
          <FormErrorMessage>
            <FormErrorIcon />
            {translate(`errors.blank`)}
          </FormErrorMessage>
        )}
      </FormControl>
    </Box>
  );
};

export default memo(SelectPins);
